import React, { useRef } from 'react'
import type { VisualState, VisualShape } from 'types'
import { useCanvasLoop } from './useCanvasLoop'
import { useFieldMode } from '../hooks/useFieldMode'
import type { CanvasSurface } from '../../utils/visuals/surface-tune'
import type { LivingFieldMode } from '../../utils/visuals/living-field-engine'

interface Props {
  visualState: VisualState
  surface?: CanvasSurface
  onShapeChange?: ((shape: VisualShape) => void) | undefined
  userProfile?: { name?: string; email?: string } | null
  isDarkMode?: boolean
  getAudioData?: (() => { waveform?: Uint8Array; frequency?: Uint8Array } | null) | undefined
  calm?: boolean | undefined
  opacity?: number | undefined
  livingModeOverride?: LivingFieldMode | undefined
}

function defaultFieldOpacity(surface: CanvasSurface, isDarkMode: boolean): number {
  if (surface === 'chat') return isDarkMode ? 0.52 : 0.22
  if (surface === 'landing') return isDarkMode ? 0.62 : 0.28
  return isDarkMode ? 0.58 : 0.3
}

function AntigravityCanvas({
  visualState,
  surface = 'page',
  onShapeChange,
  userProfile: _up,
  isDarkMode = false,
  getAudioData,
  calm = false,
  opacity,
  livingModeOverride,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { mode, showBadge } = useFieldMode()
  const { onPointerMove, onPointerDown, onPointerUp, onPointerLeave } = useCanvasLoop({
    canvasRef,
    containerRef,
    visualState,
    isDarkMode,
    surface,
    onShapeChange,
    getAudioData,
    calm,
    livingModeOverride,
  })

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        mixBlendMode: 'normal',
        opacity: opacity ?? defaultFieldOpacity(surface, isDarkMode),
      }}
    >
      <canvas
        ref={canvasRef}
        onPointerMove={onPointerMove}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      />
      {showBadge && surface !== 'chat' ? <FieldModeBadge mode={mode} /> : null}
    </div>
  )
}

function FieldModeBadge({ mode }: { mode: string }) {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        zIndex: 60,
        padding: '6px 10px',
        border: '1px solid rgba(40,48,56,0.22)',
        borderRadius: 2,
        background: 'rgba(255,255,255,0.86)',
        color: '#111315',
        font: '500 11px/1 ui-monospace, SFMono-Regular, Menlo, monospace',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        pointerEvents: 'none',
      }}
    >
      {mode} · shift+f
    </div>
  )
}

export default React.memo(AntigravityCanvas)
